/**
 * Capacitor Initialization
 * Sets up Capacitor plugins and native features for both iOS and Android.
 */

import { Capacitor } from '@capacitor/core';
import offlineSyncService from '../services/offlineSync';
import connectivityService from '../services/connectivityService';
import notificationService from '../services/notificationService';
import dynamicUIConfigService from '../services/dynamicUIConfigService';
import geofenceService from '../services/geofenceService';

export const PLATFORM = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
export const IS_IOS = PLATFORM === 'ios';
export const IS_ANDROID = PLATFORM === 'android';
export const IS_NATIVE = Capacitor.isNativePlatform();

/**
 * Initialize Capacitor for native app (iOS & Android).
 */
export async function initializeCapacitor() {
  if (!IS_NATIVE) {
    console.log(`Running in web browser – Capacitor features limited.`);
    return;
  }

  console.log(`Initializing Capacitor on ${PLATFORM}…`);

  // ── Geofence strict mode (native only) ────────────────────────────────────
  try {
    geofenceService.setStrictMode(true);
  } catch (_) {}

  // ── Status Bar ─────────────────────────────────────────────────────────────
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#0ea5e9' });
    if (IS_IOS) {
      // On iOS the StatusBar overlays the WebView by default; make sure it doesn't.
      await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
    }
  } catch (error) {
    console.warn('StatusBar not available:', error);
  }

  // ── Safe Area handling (iOS specific) ──────────────────────────────────────
  if (IS_IOS) {
    // Apply CSS safe area vars so notch/home-indicator are respected
    document.documentElement.style.setProperty(
      '--safe-area-top',
      'env(safe-area-inset-top, 0px)'
    );
    document.documentElement.style.setProperty(
      '--safe-area-bottom',
      'env(safe-area-inset-bottom, 0px)'
    );
  }

  // ── Connectivity Service ───────────────────────────────────────────────────
  try {
    await connectivityService.initialize();
    console.log('ConnectivityService initialized');
  } catch (error) {
    console.error('Error initializing ConnectivityService:', error);
  }

  // ── Push Notifications ─────────────────────────────────────────────────────
  // NOTE: Disabled on iOS until APNs / FCM is configured (avoids crash).
  // To enable: add GoogleService-Info.plist (iOS) / google-services.json (Android).
  try {
    if (IS_ANDROID) {
      await notificationService.initialize();
      console.log('NotificationService initialized (Android)');
    } else {
      console.log('NotificationService: skipped on iOS (APNs not configured yet)');
    }
  } catch (error) {
    console.warn('NotificationService: Initialization skipped:', error?.message);
  }

  // ── Dynamic UI Config ─────────────────────────────────────────────────────
  try {
    await dynamicUIConfigService.initialize();
    console.log('DynamicUIConfigService initialized');
  } catch (error) {
    console.error('Error initializing DynamicUIConfigService:', error);
  }

  // ── App lifecycle events ───────────────────────────────────────────────────
  try {
    const { App } = await import('@capacitor/app');

    App.addListener('appStateChange', ({ isActive }) => {
      console.log('App active:', isActive);
      if (isActive) {
        offlineSyncService.forceSync().catch(console.error);
        dynamicUIConfigService.fetchConfig().catch(console.error);
      }
    });

    App.addListener('appUrlOpen', (data) => {
      console.log('App opened with URL:', data.url);
      // Deep-link handling goes here if needed
    });

    // Back button – Android only; iOS uses swipe-back gesture natively
    if (IS_ANDROID) {
      App.addListener('backButton', ({ canGoBack }) => {
        try {
          if (canGoBack) {
            window.history.back();
          } else {
            App.exitApp();
          }
        } catch (_) {
          window.history.back();
        }
      });
    }
  } catch (error) {
    console.warn('App plugin not available:', error);
  }

  // ── Prevent Capgo Updater watchdog rollback on native app ──────────────────
  if (IS_NATIVE) {
    try {
      const { registerPlugin } = await import('@capacitor/core');
      const CapacitorUpdater = registerPlugin('CapacitorUpdater');
      if (CapacitorUpdater && typeof CapacitorUpdater.notifyAppReady === 'function') {
        await CapacitorUpdater.notifyAppReady();
        console.log('CapacitorUpdater: notifyAppReady() called to confirm build & prevent rollback');
      }
    } catch (_) {}
  }

  console.log(`Capacitor initialized successfully on ${PLATFORM}`);
}

/**
 * Check if running in native app (iOS or Android)
 */
export function isNativeApp() {
  return IS_NATIVE;
}

/**
 * Get platform name: 'ios' | 'android' | 'web'
 */
export function getPlatform() {
  return PLATFORM;
}
